<script context="module" lang="ts">
	export type positions =
		| 'left'
		| 'right'
		| 'top'
		| 'bottom'
		| 'bottom-right'
		| 'bottom-left'
		| 'inline-start'
		| 'inline-end'
		| 'bottom-inline-start'
		| 'bottom-inline-end'
		| 'default';
</script>

<script lang="ts">
	import { fade } from 'svelte/transition';
	import { afterUpdate } from 'svelte';
	import { isRTL } from '$lib/stores/directionStore';

	export let position: positions = 'bottom';
	export let disabled = false;
	export let isOpen = false;
	export let closeOnClick = false;
	export let color = '';
	export let disableOutsideClick = false;
	export let stopImmediatePropagation = false;
	export let maxHeight: string | undefined = undefined;

	let targetRef: HTMLElement, contentRef: HTMLElement, arrowRef: HTMLElement;
	let styledContent: string, styledArrow: string;

	let _position = position;
	afterUpdate(() => {
		switch (_position) {
			case 'inline-start':
				_position = $isRTL ? 'right' : 'left';
				break;
			case 'inline-end':
				_position = $isRTL ? 'left' : 'right';
				break;
			case 'bottom-inline-start':
				_position = $isRTL ? 'bottom-left' : 'bottom-right';
				break;
			case 'bottom-inline-end':
				_position = $isRTL ? 'bottom-right' : 'bottom-left';
				break;
		}
		if (_position === 'default') return;
		if (targetRef && contentRef && arrowRef) {
			const target = targetRef.getBoundingClientRect();
			const content = contentRef.getBoundingClientRect();
			const arrow = arrowRef.getBoundingClientRect();
			switch (_position) {
				case 'left':
					styledContent = `right:${target.width + arrow.width}px`;
					styledArrow = `right:${target.width}px;top:${target.height / 2 - arrow.height / 4}px`;
					break;

				case 'right':
					styledContent = `left:${target.width + arrow.width}px`;
					styledArrow = `left:${target.width}px;top:${target.height / 2 - arrow.height / 4}px`;
					break;

				case 'top':
					styledContent = `bottom:${target.height + arrow.height * 2}px;right:-${
						content.width / 2 - target.width / 2
					}px`;
					styledArrow = `bottom:${target.height + arrow.height}px;left:${
						target.width / 2 - arrow.width / 2
					}px`;
					break;
				case 'bottom':
					styledContent = `top:${target.height + arrow.height * 2}px;right:-${
						content.width / 2 - target.width / 2
					}px`;
					styledArrow = `top:${target.height + arrow.height}px;left:${
						target.width / 2 - arrow.width / 2
					}px`;
					break;

				case 'bottom-right':
					styledContent = `top:${target.height + arrow.height * 2}px;right:-${arrow.height * 2}px`;
					styledArrow = `top:${target.height + arrow.height}px;left:${
						target.width / 2 - arrow.width / 2
					}px`;
					break;

				case 'bottom-left':
					styledContent = `top:${target.height + arrow.height * 2}px;left:-${arrow.height * 2}px`;
					styledArrow = `top:${target.height + arrow.height}px;left:${
						target.width / 2 - arrow.width / 2
					}px`;
					break;

				default:
					return;
			}
		}
	});

	const handleClickOutside = (e: Event) => {
		if (disableOutsideClick) return;
		const target = e.target as Node;
		if (targetRef.contains(target)) {
			isOpen = !isOpen;
			stopImmediatePropagation && e.stopImmediatePropagation();
		} else if (isOpen && contentRef && contentRef.contains(target)) {
			isOpen = !closeOnClick;
		} else {
			isOpen = false;
		}
	};
</script>

<svelte:body on:click|capture={handleClickOutside} />

<div style:--arrowColor={color != '' ? color : '#fafafa'} class="placeholder">
	<span bind:this={targetRef} class="targetHolder">
		<slot name="target" />
	</span>
	{#if isOpen && !disabled}
		<span
			class="content"
			style={`${styledContent};${maxHeight ? `max-height:${maxHeight};overflow:auto` : ''}`}
			bind:this={contentRef}
			transition:fade>
			<slot name="content" />
		</span>
		<div class={`arrow ${_position}`} style={styledArrow} bind:this={arrowRef} transition:fade />
	{/if}
</div>

<style>
	.targetHolder {
		position: relative;
	}

	.targetHolder :global(*) {
		cursor: pointer;
	}

	.placeholder {
		position: relative;
	}

	.content {
		position: absolute;
		z-index: var(--z-index-Popover);
		overflow: hidden;
		border-radius: var(--gutterSmaller);
		background-color: var(--grayLightest);
		box-shadow: rgb(0 0 0 / 20%) 0 0 18px 0; /* hard coded */
		inset-block-start: 0;
	}

	.forSelectMenu {
		max-block-size: 250px;
		inline-size: 100%;
		overflow-y: auto;
	}

	.forSelectMenu::-webkit-scrollbar {
		display: none;
	}

	.arrow {
		position: absolute;
		z-index: var(--z-index-Popover);
		block-size: 0;
		inline-size: 0;
	}

	/* stylelint-disable */
	.left {
		border-block: 10px solid transparent;
		border-left: 10px solid var(--arrowColor);
	}

	.right {
		border-block: 10px solid transparent;
		border-right: 10px solid var(--arrowColor);
	}

	.bottom {
		border-inline: 10px solid transparent;
		border-block-end: 10px solid var(--arrowColor);
	}

	.top {
		border-inline: 10px solid transparent;
		border-block-start: 10px solid var(--arrowColor);
	}
</style>
